import { fetchAlarms, fetchKpiCurrent, fetchPvAssetSummary } from './api.ts';

export interface PvWarmSnapshot {
  siteId: string;
  pvSummary: unknown;
  kpiMetrics: unknown;
  alarms: unknown[];
  refreshedAt: number;
}

type PvWarmListener = (snapshot: PvWarmSnapshot) => void;

type PvWarmSiteState = {
  snapshot: PvWarmSnapshot | null;
  inFlight: boolean;
  timerId: number | null;
  listeners: Set<PvWarmListener>;
};

const pvWarmStateBySite = new Map<string, PvWarmSiteState>();

function getSiteState(siteId: string): PvWarmSiteState {
  let state = pvWarmStateBySite.get(siteId);
  if (!state) {
    state = {
      snapshot: null,
      inFlight: false,
      timerId: null,
      listeners: new Set(),
    };
    pvWarmStateBySite.set(siteId, state);
  }
  return state;
}

function normalizePayload(payload: any) {
  const first = payload?.data ?? payload;
  return first?.summary ?? first;
}

function isLikelyPvAlarm(alarm: any): boolean {
  const category = String(alarm?.category || '').toLowerCase();
  const message = String(alarm?.message || alarm?.title || '').toLowerCase();
  const registerName = String(alarm?.registerName || '').toLowerCase();
  const haystack = `${category} ${message} ${registerName}`;

  const hasPvToken = /(pv|solar|inverter|inv_)/i.test(haystack);
  const hasBessToken = /(bess|battery|pcs|soc|soh|rack|cell|charge|discharge)/i.test(haystack);

  if (!hasPvToken) return false;
  if (hasBessToken && !/(pv|solar|inverter|inv_)/i.test(haystack)) return false;
  return true;
}

function notifyListeners(siteId: string, snapshot: PvWarmSnapshot): void {
  const state = getSiteState(siteId);
  state.listeners.forEach((listener) => {
    try {
      listener(snapshot);
    } catch (error) {
      console.warn('[pvWarmCache] listener failed:', error);
    }
  });
}

async function fetchPvSnapshot(siteId: string): Promise<PvWarmSnapshot> {
  const [pvSummaryResult, kpiResult, alarmsResult] = await Promise.allSettled([
    fetchPvAssetSummary(siteId),
    fetchKpiCurrent(siteId),
    fetchAlarms({ siteId, state: 'active', category: 'pv', limit: 200 } as any),
  ]);

  const pvSummary = pvSummaryResult.status === 'fulfilled' ? normalizePayload(pvSummaryResult.value) : null;
  const kpiPayload = kpiResult.status === 'fulfilled' ? normalizePayload(kpiResult.value) : null;
  const kpiMetrics = kpiPayload?.metrics || kpiPayload || null;
  const alarmsRaw = alarmsResult.status === 'fulfilled' ? alarmsResult.value : [];
  let alarms = (Array.isArray(alarmsRaw) ? alarmsRaw : []).filter(isLikelyPvAlarm);

  // Backward compatibility: if category filter is not populated in backend,
  // fallback to site active alarms and filter client-side for PV/inverter context.
  if (!alarms.length) {
    try {
      const siteAlarmsRaw = await fetchAlarms({ siteId, state: 'active', limit: 200 } as any);
      const siteAlarms = Array.isArray(siteAlarmsRaw) ? siteAlarmsRaw : [];
      alarms = siteAlarms.filter(isLikelyPvAlarm);
    } catch {
      // keep empty alarms on fallback failure
    }
  }

  return {
    siteId,
    pvSummary,
    kpiMetrics,
    alarms,
    refreshedAt: Date.now(),
  };
}

async function refreshPvSnapshot(siteId: string): Promise<PvWarmSnapshot> {
  const state = getSiteState(siteId);
  if (state.inFlight) {
    return state.snapshot || (await fetchPvSnapshot(siteId));
  }

  state.inFlight = true;
  try {
    const snapshot = await fetchPvSnapshot(siteId);
    state.snapshot = snapshot;
    notifyListeners(siteId, snapshot);
    return snapshot;
  } finally {
    state.inFlight = false;
  }
}

export function getPvWarmSnapshot(siteId: string): PvWarmSnapshot | null {
  return getSiteState(siteId).snapshot;
}

export async function primePvWarmSnapshot(siteId: string): Promise<PvWarmSnapshot> {
  return refreshPvSnapshot(siteId);
}

export function subscribePvWarmSnapshot(siteId: string, listener: PvWarmListener): () => void {
  const state = getSiteState(siteId);
  state.listeners.add(listener);

  if (state.snapshot) {
    listener(state.snapshot);
  }

  return () => {
    state.listeners.delete(listener);
  };
}

export function startPvWarmPolling(siteId: string, intervalMs = 20_000): void {
  const state = getSiteState(siteId);
  if (state.timerId) return;

  const tick = () => {
    void refreshPvSnapshot(siteId).catch((error) => {
      console.warn('[pvWarmCache] refresh failed:', error);
    });
  };

  tick();
  state.timerId = window.setInterval(tick, intervalMs);
}
